<template>

  <div class="sp_submissionList_wr">
    <b-row
      class="content-header"
    >

      <!-- Content Left -->
      <b-col
        class="content-header-left mb-2"
        cols="12"
        md="12"
      >
        <b-row class="breadcrumbs-top">
          <b-col cols="12">
            <h2 class="custom-header-title float-left pr-1 mb-0">
              Submissions & Payments
            </h2>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- Table Container Card -->
    <b-card
      no-body
      class="mb-0 sp_submission_table"
    >

      <div class="m-2">

        <!-- Table Top -->
        <b-row>

          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          />

          <!-- Search -->
          <b-col
            cols="12"
            md="6"
          >
            <div class="d-flex align-items-center justify-content-end">
              <a
                href="javascript:void(0)"
                class="nav-link nav-link-search"
                @click="showSearchBar = !showSearchBar"
              >
                <feather-icon
                  icon="SearchIcon"
                  size="21"
                  class="mr-50"
                />
                <span class="align-middle">Search</span>
              </a>
              <b-input-group
                v-if="showSearchBar"
                class="input-group-merge mr-50"
              >
                <b-form-input
                  v-model="searchQuery"
                  placeholder="Search..."
                />
                <b-input-group-append is-text>
                  <feather-icon
                    icon="XIcon"
                    class="cursor-pointer"
                    @click="searchQuery = ''; showSearchBar = !showSearchBar"
                  />
                </b-input-group-append>
              </b-input-group>
            </div>
          </b-col>
        </b-row>
      </div>

      <b-table
        ref="refSubmissionsTable"
        class="position-relative min-height-table"
        :items="itemListData"
        responsive
        :fields="tableColumns"
        primary-key="_id"
        :sort-by.sync="sortBy"
        show-empty
        no-sort-reset
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
      >
        <template #cell(stringID)="data">
          <div class="text-nowrap">
            <b-button
              v-if="canViewThisAction('show', 'SalesSubmission')"
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="link"
              class="weight-600 align-middle detail-view-id"
              :to="{ name: 'sales-submissions-show', params: { id: data.item._id } }"
            >
              {{ data.item.stringID }}
            </b-button>
            <b-button
              v-else
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="none"
              class="text-bold-black align-middle detail-view-id not-button"
            >
              {{ data.item.stringID }}
            </b-button>
          </div>
        </template>

        <template #cell(customerName)="data">
          <div class="text-nowrap">
            <b-button
              v-if="canViewThisAction('show', 'Customer') && data.item.customer"
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="link"
              class="weight-600 align-middle detail-view-id"
              :to="{ name: 'customers-customers-show', params: { id: data.item.customer } }"
            >
              {{ data.item.customerName }}
            </b-button>
            <span v-else>{{ data.item.customerName }}</span>
          </div>
        </template>

        <template #cell(customerContact)="data">
          <div class="text-nowrap">
            <span class="">{{ data.item.customerContact || '–' }}</span>
            <br>
            <span class="">{{ data.item.customerEmail || '–' }}</span>
          </div>
        </template>

        <template #cell(agent)="data">
          <div
            v-if="data.item.mainAgent || data.item.agent"
            class=""
          >
            <b-button
              v-if="canViewThisAction('show', 'Agent')"
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="link"
              class="weight-600 align-middle detail-view-id"
              :to="data.item.mainAgent
                ? { name: 'agency-agents-show', params: { id: data.item.mainAgent } }
                : { name: 'agency-agents-probationary-show', params: { id: data.item.agent } }"
            >
              {{ data.item.agentName }}
            </b-button>
            <b-button
              v-else
              v-ripple.400="'rgba(30, 30, 30, 0.15)'"
              variant="none"
              class="text-bold-black align-middle not-button"
            >
              {{ data.item.agentName }}
            </b-button>
            <br>
            <span
              v-if="data.item.agentCode"
              class=""
            >
              {{ data.item.agentCode }}
            </span>
          </div>
          <span v-else>––</span>
        </template>

        <template #cell(itemsCount)="data">
          <div class="text-nowrap">
            <span class="">{{ data.item.itemsCount }}</span>
          </div>
        </template>

        <template #cell(orderTotal)="data">
          <div class="text-nowrap">
            <span class="text-bold-black">${{ numberFormat(data.item.orderTotal) }}</span>
          </div>
        </template>

        <template #cell(totalPayable)="data">
          <div class="text-nowrap">
            <span class="text-bold-black">${{ numberFormat(data.item.totalPayable) }}</span>
            <br>
            <span class="">{{ resolvePaymentTypeLabel(data.item.paymentType) }}</span>
          </div>
        </template>

        <template #cell(paymentStatus)="data">
          <b-badge
            v-if="data.item.paymentStatus"
            :variant="resolvePaymentStatusVariant(data.item.paymentStatus, data.item.paymentMethod)"
            class="text-capitalize"
          >
            {{ resolvePaymentStatusLabel(data.item.paymentStatus, data.item.paymentMethod) }}
          </b-badge>
          <span v-else>–</span>
        </template>

        <template #cell(createdAt)="data">
          <div class="text-nowrap">
            <span class="">{{ dateFormatWithTime(data.item.createdAt) }}</span>
          </div>
        </template>

        <!-- Column: Actions -->
        <template
          v-if="canViewThisAction('show', 'SalesSubmission')"
          #cell(actions)="data"
        >
          <b-dropdown
            variant="link"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="canViewThisAction('show', 'SalesSubmission')"
              :to="{ name: 'sales-submissions-show', params: { id: data.item._id } }"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">View</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="canViewThisAction('register-payment', 'SalesSubmission') && data.item.itemsCount > 0 && (!data.item.paymentMethod || (data.item.paymentMethod === 'Counter' && data.item.paymentStatus === 'counter-pending'))"
              :to="{ name: 'sales-submissions-register-payment', params: { id: data.item._id } }"
            >
              <feather-icon icon="DollarSignIcon" />
              <span class="align-middle ml-50">Register Payment</span>
            </b-dropdown-item>
          </b-dropdown>
        </template>

      </b-table>
    </b-card>

    <div
      v-if="totalSubmissions > perPage"
      class="mx-2 mb-2"
    >
      <b-row>
        <!-- Pagination -->
        <b-col
          cols="12"
          sm="12"
          class="d-flex align-items-center justify-content-center pagination-content"
        >

          <b-pagination
            v-model="currentPage"
            :total-rows="totalSubmissions"
            :per-page="perPage"
            first-number
            last-number
            class="mb-0 mt-1 mt-sm-0"
            align="center"
            prev-text="Prev"
            next-text="Next"
          >
            <template #prev-text>
              <feather-icon
                icon="ChevronLeftIcon"
                size="18"
              />
              <span>Prev</span>
            </template>
            <template #next-text>
              <span>Next</span>
              <feather-icon
                icon="ChevronRightIcon"
                size="18"
              />
            </template>
          </b-pagination>

        </b-col>

      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard, BRow, BButton, BCol, BFormInput, BTable, BInputGroup, BInputGroupAppend,
  BDropdown, BDropdownItem, BPagination, BBadge,
} from 'bootstrap-vue'
import { ref, onUnmounted } from '@vue/composition-api'
// eslint-disable-next-line import/no-cycle
import { avatarText } from '@core/utils/filter'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import store from '@/store'
import { useUtils as useAclUtils } from '@/libs/acl/custom'
// eslint-disable-next-line import/no-cycle
import useSubmissionList from './useSubmissionList'
import submissionStoreModule from './submissionStoreModule'

export default {
  components: {
    BCard,
    BRow,
    BButton,
    BCol,
    BFormInput,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BInputGroup,
    BInputGroupAppend,
    BBadge,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
  },
  directives: {
    Ripple,
  },
  setup() {
    const showSearchBar = ref(false)
    const SALES_SUBMISSION_APP_STORE_MODULE_NAME = 'app-sales-submissions'

    // Register module
    if (!store.hasModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)) store.registerModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME, submissionStoreModule)

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)) store.unregisterModule(SALES_SUBMISSION_APP_STORE_MODULE_NAME)
    })

    const { canViewThisAction } = useAclUtils()

    const {
      fetchSubmissions,
      tableColumns,
      perPage,
      currentPage,
      totalSubmissions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSubmissionsTable,
      refetchData,
      currentDataLength,
      itemListData,

      resolvePaymentStatusVariant,
      resolvePaymentStatusLabel,
      resolvePaymentTypeLabel,

      // Extra Filters
      statusFilter,
      createdAtFilter,
    } = useSubmissionList()

    if (searchQuery.value) {
      showSearchBar.value = true
    }

    return {
      fetchSubmissions,
      tableColumns,
      perPage,
      currentPage,
      totalSubmissions,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refSubmissionsTable,
      refetchData,
      showSearchBar,
      currentDataLength,
      itemListData,

      avatarText,
      canViewThisAction,

      resolvePaymentStatusVariant,
      resolvePaymentStatusLabel,
      resolvePaymentTypeLabel,

      // Extra Filters
      statusFilter,
      createdAtFilter,
    }
  },
}
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
